/**
 * Iter 73 Phase 2 + 3 + 4 + 9 — Right Inspector tabbed panel for the
 * Sheet Viewer. Replaces the legacy Pins-only sidebar with a tabbed
 * surface that surfaces:
 *   - Pins (existing)
 *   - Markups (Iter 73 Phase 1 — persistent)
 *   - Measurements (Iter 73 Phase 3 — polished)
 *   - Objects (Iter 73 Phase 4 — project registry)
 *   - Activity (link to /activity)
 *
 * Empty states (Phase 9) are honest — they educate without lying.
 * All tabs except Pins use the new persistence APIs in lib/markupsApi.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  PenSquare, Ruler, Hammer, MapPin, Activity as ActivityIcon, X, Trash2,
  Layers as LayersIcon, GitCompare, Link2, AlertTriangle,
} from "lucide-react";
import {
  listMarkups, deleteMarkup,
  listProjectObjects, projectObjectsSummary,
  patchMeasurement,
} from "@/lib/markupsApi";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { enqueue } from "@/lib/syncQueue";
import { enqueue as offlineEnqueue, listQueue } from "@/lib/offlineQueueV2";
import { measurementDisplayLabel } from "@/lib/units";
import LayersPanel from "@/components/viewer/LayersPanel";
import RevisionWorkspace from "@/components/viewer/RevisionWorkspace";

const TABS = [
  { id: "pins",         label: "Pins",      icon: MapPin },
  { id: "markups",      label: "Markups",   icon: PenSquare },
  { id: "measurements", label: "Measure",   icon: Ruler },
  { id: "objects",      label: "Objects",   icon: Hammer },
  { id: "layers",       label: "Layers",    icon: LayersIcon },
  { id: "revisions",    label: "Revs",      icon: GitCompare },
  { id: "activity",     label: "Activity",  icon: ActivityIcon },
];

export default function SheetInspectorPanel({
  projectId, sheetId, tenantId,
  pins, activePin, setActivePin, PIN_CATEGORY,
  recentList, onOpenSheet,
  StatusBadge, PriorityBadge,
  markupState,
  defaultTab = "pins",
  onMeasurementsChanged,
}) {
  const [tab, setTab] = useState(defaultTab);
  // Iter 73 — allow other parts of the viewer (e.g. left tool rail) to
  // switch the inspector tab via a window event.
  useEffect(() => {
    const h = (e) => {
      const t = e?.detail?.tab;
      if (t && TABS.some((x) => x.id === t)) setTab(t);
    };
    window.addEventListener("forgedops:inspector-tab", h);
    return () => window.removeEventListener("forgedops:inspector-tab", h);
  }, []);
  // Iter 74 Phase 4 — tap on a utility / object on the canvas routes to Objects tab.
  useEffect(() => {
    const h = () => setTab("objects");
    window.addEventListener("forgedops:object-tap", h);
    return () => window.removeEventListener("forgedops:object-tap", h);
  }, []);
  return (
    <aside
      className="border-l border-[var(--fop-border)] bg-[var(--fop-surface)] overflow-hidden flex flex-col w-full lg:min-w-[280px] lg:max-w-[360px]"
      data-testid="sheet-inspector"
    >
      {/* Iter 112 F-007 — surface existing references[] data so field
          users can see what specs / RFIs / docs link this sheet.
          Renders nothing when the sheet has no references. */}
      <SheetReferencesStrip sheetId={sheetId} />
      {/* Iter 178 — Right rail tab strip redesign.
          Matches the left rail's elite treatment: white-ink icons, left-
          side orange accent when active, tighter uniform typography,
          layered surface. Horizontally scrollable at narrow widths so
          all 7 tabs remain legible without shrinking below 44px. */}
      <div className="relative border-b border-[var(--fop-border)]">
        <div
          className="flex overflow-x-auto"
          data-testid="inspector-tabs"
          style={{
            scrollbarWidth: "none",
            background: "linear-gradient(180deg, var(--fop-surface-2) 0%, var(--fop-surface) 100%)",
          }}
        >
          {TABS.map(({ id, label, icon: Icon }) => {
            const on = tab === id;
            return (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`inspector-tab-${id}`}
                data-active={on ? "true" : "false"}
                className={`group relative shrink-0 flex flex-col items-center justify-center gap-1 py-2.5 px-3 transition-all min-w-[64px] ${
                  on
                    ? "text-[var(--fop-primary)]"
                    : "text-[var(--fop-text)] hover:text-white hover:bg-[var(--fop-surface-3)]/60"
                }`}
                style={{ minHeight: 56 }}
                title={label}
              >
                {/* Active-tab indicator — top-edge orange bar (mirror of the
                    left rail's active accent). No border-bottom trick so the
                    strip's separator line stays clean. */}
                {on && (
                  <span
                    aria-hidden="true"
                    className="absolute top-0 left-3 right-3 h-[3px] rounded-b-sm bg-[var(--fop-primary)]"
                  />
                )}
                <Icon
                  size={16}
                  strokeWidth={on ? 2.5 : 2}
                  className={on ? "" : "opacity-90 group-hover:opacity-100"}
                />
                <span
                  className={`text-[10.5px] font-mono uppercase tracking-wider whitespace-nowrap leading-none ${
                    on ? "font-bold" : "font-semibold opacity-85 group-hover:opacity-100"
                  }`}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </div>
        {/* Hard-Gate visual cert (2026-09-07) — the strip legitimately
            overflows at narrow widths (7 tabs, 44px+ touch targets each).
            A right-edge fade makes that scrollability visually obvious
            rather than looking like a hard clip, per Founder's "contained,
            obvious, and usable" rule for intentional horizontal scroll. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-0 right-0 h-full w-6"
          style={{ background: "linear-gradient(90deg, transparent, var(--fop-surface))" }}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {tab === "pins" && (
          <PinsTab
            pins={pins} activePin={activePin} setActivePin={setActivePin}
            PIN_CATEGORY={PIN_CATEGORY} StatusBadge={StatusBadge} PriorityBadge={PriorityBadge}
          />
        )}
        {tab === "markups" && (
          <MarkupsTab projectId={projectId} sheetId={sheetId} markupState={markupState} />
        )}
        {tab === "measurements" && (
          <MeasurementsTab sheetId={sheetId} onMeasurementsChanged={onMeasurementsChanged} />
        )}
        {tab === "objects" && (
          <ObjectsTab projectId={projectId} sheetId={sheetId} />
        )}
        {tab === "layers" && (
          <LayersPanel
            projectId={projectId} sheetId={sheetId}
            tenantId={tenantId}
            pinsCount={pins?.length || 0}
            markupsCount={markupState?.markups?.length || 0}
          />
        )}
        {tab === "revisions" && (
          <RevisionWorkspace projectId={projectId} sheetId={sheetId} />
        )}
        {tab === "activity" && (
          <ActivityTab projectId={projectId} sheetId={sheetId} />
        )}
      </div>
    </aside>
  );
}

/* -------------------- Pins (existing behaviour preserved) -------------------- */
function PinsTab({ pins, activePin, setActivePin, PIN_CATEGORY, StatusBadge, PriorityBadge }) {
  return (
    <div>
      <PanelHeader label={`Pins · ${pins.length}`} testid="pins-header" />
      {pins.length === 0 ? (
        <EmptyState
          title="No pins yet"
          body="Pins flag field issues, safety items, RFIs, inspections, and survey requests at exact locations on the plan. Drop your first one to start tracking field coordination."
        />
      ) : (
        <ul>
          {pins.map((p) => {
            const cat = (PIN_CATEGORY && PIN_CATEGORY[p.category]) || {};
            return (
              <li key={p.id}>
                <button
                  onClick={() => setActivePin(p.id)}
                  className={`w-full text-left px-3 py-2.5 border-b border-[var(--fop-border)] hover:bg-[var(--fop-surface-2)] flex gap-2.5 transition-colors ${activePin === p.id ? "bg-[var(--fop-surface-2)]" : ""}`}
                  data-testid={`pin-list-${p.id}`}
                >
                  <div className="w-0.5 self-stretch rounded-full" style={{ background: cat.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[var(--fop-text)] truncate font-medium">{p.title}</div>
                    <div className="flex gap-1.5 mt-1.5 items-center">
                      {StatusBadge && <StatusBadge status={p.status} />}
                      {PriorityBadge && <PriorityBadge priority={p.priority} />}
                      {p.carried_forward_from_id && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[9px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]"
                          data-testid={`pin-carried-badge-${p.id}`}
                          title="Carried forward from the prior revision"
                        >
                          <GitCompare size={9} /> carried
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* -------------------- Markups (persistent — Iter 73 Phase 1) -------------------- */
function MarkupsTab({ projectId, sheetId, markupState }) {
  const markups = markupState?.markups || [];
  return (
    <div>
      <PanelHeader label={`Markups · ${markups.length}`} testid="markups-header" />
      {markups.length === 0 ? (
        <EmptyState
          title="No markups yet"
          body="Add field notes, callouts, or highlights to begin sheet-level coordination. Every markup is saved with author and timestamp, and persists across refresh, sheet switch, and offline sessions."
        />
      ) : (
        <ul data-testid="markups-list">
          {markups.map((m) => (
            <li key={m.id} className="px-3 py-2.5 border-b border-[var(--fop-border)] flex items-start gap-2.5" data-testid={`markup-row-${m.id}`}>
              <div className="w-0.5 self-stretch rounded-full mt-0.5" style={{ background: m.color || "#ff6a1a" }} />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[var(--fop-text)] font-medium truncate">
                  {m.label || m.text || (m.type || "markup")}
                </div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5">
                  {m.type}{m.created_by_name ? ` · ${m.created_by_name}` : ""}
                </div>
              </div>
              <button
                onClick={() => markupState.remove(m.id)}
                title="Delete markup"
                data-testid={`markup-delete-${m.id}`}
                className="opacity-60 hover:opacity-100 text-[var(--fop-text-3)] hover:text-[var(--fop-danger)] p-1"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* -------------------- Measurements (Iter 73 Phase 3) -------------------- */
function MeasurementsTab({ sheetId, onMeasurementsChanged }) {
  const [rows, setRows] = useState(null); // null = loading
  const [scale, setScale] = useState(null);
  const [calib, setCalib] = useState(null);
  const reload = useCallback(async () => {
    try {
      const [m, s, c] = await Promise.all([
        api.get(`/sheets/${sheetId}/measurements`),
        api.get(`/sheets/${sheetId}/scale`).catch(() => ({ data: null })),
        api.get(`/sheets/${sheetId}/calibration`).catch(() => ({ data: null })),
      ]);
      setRows(Array.isArray(m.data) ? m.data : []);
      setScale(s.data);
      setCalib(c.data);
    } catch (e) {
      setRows([]);
    }
  }, [sheetId]);
  useEffect(() => { if (sheetId) reload(); }, [sheetId, reload]);

  const onRename = async (m) => {
    const next = window.prompt("Rename measurement", m.label || "");
    if (next === null) return;
    try {
      await patchMeasurement(m.id, { label: next });
      reload();
      onMeasurementsChanged?.();
    } catch (e) { /* keep silent — backend will error in audit */ }
  };
  const onDelete = async (m) => {
    if (!window.confirm("Delete this measurement?")) return;
    try {
      await api.delete(`/measurements/${m.id}`);
      reload();
      // MEDIUM fix (real-plan cert iteration_184) — reload() only
      // refreshes this panel's own local rows; the status-bar MEASURE
      // count and canvas overlay need the shared reload key bumped too.
      onMeasurementsChanged?.();
    } catch (e) { /* ignore */ }
  };

  // B2-05 (independent recheck batch 2, 2026-10-01) — the human review
  // step that settles a `recalibrated_pending_review` measurement.
  // B3-08 (2026-10-01 completion directive) — binds this action to the
  // EXACT `pending_review_scale_version` currently rendered on this
  // row; the server atomically rejects the request (409 `stale_review`)
  // if that's no longer the live proposal (a newer recalibration ran,
  // or this same click was already processed), instead of silently
  // approving/rejecting whatever happens to be current by then.
  const onReviewRecalibration = async (m, action) => {
    const verb = action === "approve" ? "Approve" : "Reject";
    if (!window.confirm(`${verb} this recalibration for "${m.label || m.kind}"?`)) return;
    try {
      await api.post(`/measurements/${m.id}/review-recalibration`, {
        action,
        expected_pending_review_scale_version: m.pending_review_scale_version,
      });
      toast.success(action === "approve" ? "Recalibration approved" : "Recalibration rejected — reverted to prior value");
      reload();
      onMeasurementsChanged?.();
    } catch (e) {
      if (e?.response?.status === 409 && e?.response?.data?.detail?.code === "stale_review") {
        toast.error("This recalibration proposal changed — reloading the latest value.");
        reload();
        onMeasurementsChanged?.();
        return;
      }
      toast.error(e?.response?.data?.detail?.message || "Couldn't record review");
    }
  };

  const cal = useMemo(() => {
    if (scale && scale.p1_nx != null && scale.real_distance_ft) {
      return { label: `${scale.real_distance_ft} ft @ ${scale.label || "scale"}`, set: true };
    }
    if (calib && Array.isArray(calib.points) && calib.points.length >= 2) {
      return { label: `${calib.points.length}-point GPS calibration`, set: true };
    }
    return { label: "Not calibrated", set: false };
  }, [scale, calib]);

  if (rows === null) {
    return <div className="p-4 text-xs text-[var(--fop-text-3)]">Loading…</div>;
  }
  return (
    <div>
      <PanelHeader label={`Measurements · ${rows.length}`} testid="measurements-header" />
      <div className="px-3 py-2 border-b border-[var(--fop-border)] flex items-center justify-between" data-testid="measurements-calibration">
        <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">Calibration</span>
        <span className={`text-[11px] ${cal.set ? "text-[var(--fop-success)]" : "text-[var(--fop-text-3)]"}`}>{cal.label}</span>
      </div>
      {rows.length === 0 ? (
        <EmptyState
          title="No measurements yet"
          body="Calibrate the sheet with a known distance, then drop distance, area, polyline, or coordinate measurements. Every measurement is persisted and audit-logged with author and timestamp."
        />
      ) : (
        <ul data-testid="measurements-list">
          {rows.map((m) => (
            <li key={m.id} className="px-3 py-2.5 border-b border-[var(--fop-border)] group" data-testid={`measurement-row-${m.id}`}>
              <div className="flex items-start gap-2.5">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[var(--fop-text)] font-medium truncate">
                    {measurementDisplayLabel(m) || `${m.kind}${m.value ? ` · ${m.value.toFixed?.(1) ?? m.value} ${m.unit || ""}` : ""}`}
                  </div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5">
                    {m.kind}{m.created_by_name ? ` · ${m.created_by_name}` : ""}
                    {m.carried_forward_from_id && (
                      <span className="ml-1 text-[var(--fop-text-4)]" title="Carried forward from the prior revision">· carried</span>
                    )}
                  </div>
                  {m.geometry_integrity_status === "legacy_unverified" && (
                    <div
                      className="inline-flex items-center gap-1 mt-1 text-[9px] font-mono uppercase tracking-wider text-[var(--fop-warn)]"
                      data-testid={`measurement-legacy-badge-${m.id}`}
                      title={`Re-measurement required — ${m.geometry_unverified_reason || "could not be verified"}`}
                    >
                      <AlertTriangle size={10} /> Legacy · Unverified · Re-measure
                    </div>
                  )}
                  {/* R-13 (independent recheck batch 2, 2026-10-01) — this
                     was previously shown NOWHERE in the main Inspector
                     panel at all: `scale_trust` !== "trusted" rendered
                     with zero visual distinction from a fully-trusted
                     measurement — "false trust" in the most literal
                     sense, since the operator reading this exact panel
                     while standing on the sheet had no way to know. */}
                  {m.scale_trust === "unverified" && (
                    <div
                      className="inline-flex items-center gap-1 mt-1 text-[9px] font-mono uppercase tracking-wider text-[var(--fop-warn)]"
                      data-testid={`measurement-scale-unverified-badge-${m.id}`}
                      title={m.scale_trust_reason || "This sheet's scale calibration could not be fully verified for this area — this value may be wrong"}
                    >
                      <AlertTriangle size={10} /> Scale Unverified
                    </div>
                  )}
                  {m.scale_trust === "untrusted" && (
                    <div
                      className="inline-flex items-center gap-1 mt-1 text-[9px] font-mono uppercase tracking-wider text-red-400"
                      data-testid={`measurement-scale-untrusted-badge-${m.id}`}
                      title={m.scale_trust_reason || "This sheet is marked NOT TO SCALE — this value was computed only because a human explicitly acknowledged the risk"}
                    >
                      <AlertTriangle size={10} /> Not To Scale
                    </div>
                  )}
                     distinct from the "Corrected" badge below: this
                     value changed because the sheet's calibration
                     changed, and a human has not yet confirmed it's
                     right. Must never render as a settled "Corrected"
                     result until reviewed. */}
                  {m.geometry_integrity_status === "recalibrated_pending_review" && (
                    <div
                      className="inline-flex items-center gap-1 mt-1 text-[9px] font-mono uppercase tracking-wider text-[var(--fop-warn)]"
                      data-testid={`measurement-pending-review-badge-${m.id}`}
                      title="The sheet's calibration changed since this was last measured — review before trusting this value"
                    >
                      <AlertTriangle size={10} /> Calibration changed · Pending review
                      {onReviewRecalibration && (
                        <>
                          <button
                            onClick={() => onReviewRecalibration(m, "approve")}
                            className="ml-1 underline hover:text-[var(--fop-text)]"
                            data-testid={`measurement-approve-recalibration-${m.id}`}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => onReviewRecalibration(m, "reject")}
                            className="ml-1 underline hover:text-[var(--fop-text)]"
                            data-testid={`measurement-reject-recalibration-${m.id}`}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  {m.geometry_recomputed_at && m.geometry_integrity_status !== "recalibrated_pending_review" && (
                    <div
                      className="mt-1 text-[9px] font-mono uppercase tracking-wider text-[var(--fop-warn)]"
                      data-testid={`measurement-corrected-badge-${m.id}`}
                      title="This value was corrected — an earlier coordinate-model defect made it read wrong"
                    >
                      Corrected → {m.value != null ? `${Number(m.value).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${m.unit || ""}` : "—"}
                      {m.legacy_original_value != null && (
                        <span className="text-[var(--fop-text-4)]"> (was {Number(m.legacy_original_value).toLocaleString(undefined, { maximumFractionDigits: 2 })} {m.unit || ""})</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => onRename(m)} className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)] px-1.5" data-testid={`measurement-rename-${m.id}`}>Rename</button>
                  <button onClick={() => onDelete(m)} className="text-[var(--fop-text-3)] hover:text-[var(--fop-danger)] p-1" data-testid={`measurement-delete-${m.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* -------------------- Objects (Iter 73 Phase 4) -------------------- */
const OBJECT_TYPES = [
  { id: "structure", label: "Structure" },
  { id: "pipe",      label: "Pipe" },
  { id: "utility",   label: "Utility" },
  { id: "note",      label: "Note" },
  { id: "conflict",  label: "Conflict" },
  { id: "custom",    label: "Custom" },
  // Iter 75 Phase 5 — Airfield foundation.
  { id: "runway",              label: "Runway" },
  { id: "taxiway",             label: "Taxiway" },
  { id: "apron",               label: "Apron" },
  { id: "airfield_lighting",   label: "Lighting" },
  { id: "airfield_signage",    label: "Signage" },
  { id: "hold_position",       label: "Hold Position" },
  { id: "airfield_electrical", label: "Electrical" },
  { id: "navigation_aid",      label: "Nav Aid" },
  { id: "safety_area",         label: "Safety Area" },
  { id: "obstacle",            label: "Obstacle" },
];

function ObjectsTab({ projectId, sheetId }) {
  const [rows, setRows] = useState(null);
  // B2-10 (independent recheck batch 2, 2026-10-01) — reopens R-06.
  // A denied/failed objects read used to merge straight into `rows`
  // (an empty array, or whatever pending-queue rows existed) with no
  // record anywhere that the server-side list was never actually
  // obtained — rendering identically to a genuinely empty registry.
  const [readStatus, setReadStatus] = useState("ok"); // ok | forbidden | degraded
  const [filter, setFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active"); // active | candidates | all
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [correctingId, setCorrectingId] = useState(null);
  const [correctionLabel, setCorrectionLabel] = useState("");
  const reload = useCallback(async () => {
    // FP-034 (2026-09-09 audit remediation) — a full page reload while
    // OFFLINE loses all in-memory optimistic rows AND the network call
    // below fails outright; merge in any still-queued object.create
    // entries for this sheet so a durably-queued-but-unsynced object
    // never appears to vanish just because the page reloaded.
    const pendingFromQueue = listQueue()
      .filter((e) => e.op === "object.create" && e.payload?.projectId === projectId
        && e.payload?.body?.sheet_id === sheetId)
      .map((e) => ({
        id: e.id, project_id: projectId, sheet_id: sheetId,
        type: e.payload.body.type, label: e.payload.body.label,
        description: e.payload.body.description || null,
        metadata: e.payload.body.metadata || {},
        conflict_status: "none", status: "active",
        created_by_name: "You", created_at: new Date(e.queued_at).toISOString(),
        _pending_sync: true,
      }));
    try {
      const data = await listProjectObjects(projectId, { sheet_id: sheetId });
      setReadStatus(data?._forbidden ? "forbidden" : (data?._degraded ? "degraded" : "ok"));
      // De-dupe: once a pending entry actually syncs, it disappears
      // from the queue on its own (handled by the sync-event listener
      // below); this merge only ever ADDS entries the server doesn't
      // know about yet.
      setRows([...pendingFromQueue, ...data]);
    } catch {
      // Offline / network failure — show whatever we last had server-
      // side (if any) plus the queued pending rows, rather than an
      // empty, misleading "no objects" state.
      setReadStatus("degraded");
      setRows((prev) => [...pendingFromQueue, ...((prev || []).filter((r) => !r._pending_sync))]);
    }
  }, [projectId, sheetId]);
  useEffect(() => { if (projectId && sheetId) reload(); }, [projectId, sheetId, reload]);

  // FP-034 (2026-09-09 audit remediation) — "successful sync updates
  // the displayed record correctly": once a queued object.create
  // actually lands on the server, replace the optimistic local entry
  // with the real, authoritative list — fires on BOTH manual and
  // automatic background sync (see offlineQueueV2.js's flush()).
  useEffect(() => {
    const onSynced = () => { if (projectId && sheetId) reload(); };
    window.addEventListener("forgedops:offline-synced", onSynced);
    return () => window.removeEventListener("forgedops:offline-synced", onSynced);
  }, [projectId, sheetId, reload]);

  // Iter 74 Phase 4 — clicking an object on the canvas opens the Inspector.
  useEffect(() => {
    const h = (e) => {
      const oid = e?.detail?.object_id;
      if (!oid) return;
      setSelected(oid);
    };
    window.addEventListener("forgedops:object-tap", h);
    return () => window.removeEventListener("forgedops:object-tap", h);
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return null;
    let r = rows;
    if (filter !== "all") r = r.filter((o) => o.type === filter);
    if (statusFilter === "active") {
      r = r.filter((o) => o.status === "active" || o.status === "verified");
    } else if (statusFilter === "candidates") {
      r = r.filter((o) => o.status === "extracted_candidate");
    }
    return r;
  }, [rows, filter, statusFilter]);

  if (rows === null) {
    return <div className="p-4 text-xs text-[var(--fop-text-3)]">Loading…</div>;
  }
  const candidateCount = rows.filter((o) => o.status === "extracted_candidate").length;

  // Hard-Gate §37 K-05 (2026-09-07) — real defect found while live-testing
  // the certified real-plan Utility Auto-Extraction workflow offline: these
  // 3 handlers called `api.post`/`api.patch` directly with no offline
  // fallback at all — a field user offline-verifying/correcting/rejecting a
  // genuine drainage/utility candidate (the exact certified workflow) would
  // just see a raw error and LOSE the decision entirely, unlike every other
  // object-intelligence mutation in this app (all of which already fall
  // back to `syncQueue.enqueue()` per `lib/objectIntel.js`). Now: on a
  // genuine network failure, queue the mutation (same engine already
  // rendered by `OperationalStatusSpine`'s `SyncPill`) and optimistically
  // update the local row so the human's decision is never lost or hidden.
  // Fatal 4xx (bad request / permission denied) still surfaces as an error
  // — only network/5xx/timeout failures get queued.
  const isNetworkFailure = (err) => {
    const s = err?.response?.status || 0;
    return s === 0 || s >= 500 || s === 408 || s === 425 || s === 429;
  };
  const onVerify = async (oid) => {
    try {
      await api.post(`/objects/${oid}/verify`);
      reload();
      window.dispatchEvent(new CustomEvent("forgedops:objects-updated"));
    } catch (e) {
      if (!isNetworkFailure(e)) {
        window.alert(e?.response?.data?.detail || "Failed to verify");
        return;
      }
      await enqueue({ op_type: "object.verify", endpoint: `/objects/${oid}/verify`, method: "POST", payload: {} });
      setRows((prev) => prev?.map((r) => (r.id === oid ? { ...r, status: "verified" } : r)) ?? prev);
    }
  };
  const onReject = async (oid) => {
    if (!window.confirm("Reject this extraction candidate?")) return;
    try {
      await api.post(`/objects/${oid}/reject`);
      reload();
    } catch (e) {
      if (!isNetworkFailure(e)) {
        window.alert(e?.response?.data?.detail || "Failed to reject");
        return;
      }
      await enqueue({ op_type: "object.reject", endpoint: `/objects/${oid}/reject`, method: "POST", payload: {} });
      setRows((prev) => prev?.map((r) => (r.id === oid ? { ...r, status: "rejected" } : r)) ?? prev);
    }
  };
  const onStartCorrect = (o) => { setCorrectingId(o.id); setCorrectionLabel(o.label || ""); };
  const onSubmitCorrect = async (oid) => {
    const payload = { label: correctionLabel };
    try {
      await api.patch(`/objects/${oid}/correct`, payload);
      setCorrectingId(null);
      reload();
      window.dispatchEvent(new CustomEvent("forgedops:objects-updated"));
    } catch (e) {
      if (!isNetworkFailure(e)) {
        window.alert(e?.response?.data?.detail || "Failed to correct");
        return;
      }
      await enqueue({ op_type: "object.correct", endpoint: `/objects/${oid}/correct`, method: "PATCH", payload });
      setCorrectingId(null);
      setRows((prev) => prev?.map((r) => (r.id === oid ? { ...r, status: "corrected", label: correctionLabel } : r)) ?? prev);
    }
  };

  return (
    <div>
      <PanelHeader
        label={`Objects · ${rows.length}${candidateCount ? ` · ${candidateCount} candidate${candidateCount === 1 ? "" : "s"}` : ""}`}
        testid="objects-header"
        right={
          <button
            onClick={() => setShowCreate(true)}
            className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:underline"
            data-testid="objects-add"
          >+ Add</button>
        }
      />
      {readStatus !== "ok" && (
        <div
          className="px-3 py-2 border-b border-[var(--fop-border)] flex items-center gap-3 bg-[var(--fop-surface-2)]"
          data-testid="objects-read-degraded"
        >
          <span className="text-[11px] text-[var(--st-pending,#f59e0b)]">
            {readStatus === "forbidden" ? "Access denied — object list may be incomplete" : "Couldn't refresh — list may be out of date"}
          </span>
          <button
            type="button"
            onClick={reload}
            className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-[var(--fop-border)] hover:bg-[var(--fop-surface)]"
            data-testid="objects-read-degraded-retry"
          >
            Retry
          </button>
        </div>
      )}
      {/* Iter 74 Phase 1 — status filter (active vs extracted candidates) */}
      <div className="px-3 py-2 border-b border-[var(--fop-border)] flex gap-1.5">
        <FilterChip on={statusFilter === "active"} onClick={() => setStatusFilter("active")} label="Active" />
        <FilterChip on={statusFilter === "candidates"} onClick={() => setStatusFilter("candidates")}
                    label={candidateCount ? `Candidates · ${candidateCount}` : "Candidates"} />
        <FilterChip on={statusFilter === "all"} onClick={() => setStatusFilter("all")} label="All" />
      </div>
      <div className="px-3 py-2 flex flex-wrap gap-1.5 border-b border-[var(--fop-border)]" data-testid="objects-filter">
        <FilterChip on={filter === "all"} onClick={() => setFilter("all")} label="All types" />
        {OBJECT_TYPES.map((t) => (
          <FilterChip key={t.id} on={filter === t.id} onClick={() => setFilter(t.id)} label={t.label} />
        ))}
      </div>
      {filtered.length === 0 ? (
        readStatus !== "ok" ? null : (
          <EmptyState
            title={statusFilter === "candidates" ? "No extraction candidates" : "No objects created yet"}
            body={
              statusFilter === "candidates"
                ? "When sheet extraction runs, candidate objects appear here with confidence scores. A human must verify or reject every candidate — nothing auto-promotes."
                : "Add structures, utilities, pipes, or coordination notes to build a searchable project registry. Every object can be pinned to a sheet location and linked to documents, photos, and markups."
            }
          />
        )
      ) : (
        <ul data-testid="objects-list">
          {filtered.map((o) => {
            const isCandidate = o.status === "extracted_candidate";
            const sel = selected === o.id;
            return (
              <li key={o.id}
                  className={`px-3 py-2.5 border-b border-[var(--fop-border)] hover:bg-[var(--fop-surface-2)] ${sel ? "bg-[var(--fop-surface-2)]" : ""} ${isCandidate ? "border-l-2 border-l-[var(--fop-warn)]" : ""}`}
                  data-testid={`object-row-${o.id}`}
                  onClick={() => setSelected(o.id)}>
                <div className="text-xs text-[var(--fop-text)] font-medium truncate flex items-center gap-2">
                  {o.label}
                  {isCandidate && !o.review_required && (
                    <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--fop-warn)] text-[var(--fop-warn)]"
                          data-testid={`object-badge-auto-detected-${o.id}`}>
                      Auto-detected · {Math.round((o.confidence || 0) * 100)}%
                    </span>
                  )}
                  {isCandidate && o.review_required && (
                    <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--fop-danger)] text-[var(--fop-danger)]"
                          data-testid={`object-badge-review-required-${o.id}`}>
                      Unknown · Review required · {Math.round((o.confidence || 0) * 100)}%
                    </span>
                  )}
                  {o.status === "verified" && (
                    <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-[rgba(16,185,129,0.12)] text-[var(--fop-success)]" data-testid={`object-badge-confirmed-${o.id}`}>human-confirmed</span>
                  )}
                  {o.status === "corrected" && (
                    <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-[rgba(249,115,22,0.12)] text-[var(--fop-primary)]" data-testid={`object-badge-corrected-${o.id}`}>human-corrected</span>
                  )}
                  {o.status === "rejected" && (
                    <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded text-[var(--fop-text-3)] line-through">rejected</span>
                  )}
                  {o._pending_sync && (
                    <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--fop-text-3)] text-[var(--fop-text-3)]" data-testid={`object-badge-pending-sync-${o.id}`}>
                      Pending sync
                    </span>
                  )}
                </div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5 flex gap-2">
                  <span>{o.type}</span>
                  {o.metadata?.utility_kind && <span>· {o.metadata.utility_kind}</span>}
                  {o.conflict_status && o.conflict_status !== "none" && (
                    <span style={{ color: "var(--fop-danger)" }}>· {o.conflict_status.replace(/_/g, " ")}</span>
                  )}
                </div>
                {(o.metadata?.rim || o.metadata?.invert || o.metadata?.pipe_size) && (
                  <div className="text-[10px] text-[var(--fop-text-3)] mt-0.5">
                    {o.metadata.rim ? `RIM ${o.metadata.rim} ` : ""}
                    {o.metadata.invert ? `· INV ${o.metadata.invert} ` : ""}
                    {o.metadata.pipe_size ? `· ${o.metadata.pipe_size}` : ""}
                  </div>
                )}
                {isCandidate && (
                  <div className="flex gap-2 mt-2" data-testid={`object-actions-${o.id}`}>
                    <button onClick={(e) => { e.stopPropagation(); onVerify(o.id); }}
                            data-testid={`object-verify-${o.id}`}
                            className="text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded bg-[var(--fop-success)] text-black">
                      Verify
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); onStartCorrect(o); }}
                            data-testid={`object-correct-${o.id}`}
                            className="text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded border border-[var(--fop-primary)] text-[var(--fop-primary)]">
                      Correct
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); onReject(o.id); }}
                            data-testid={`object-reject-${o.id}`}
                            className="text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded border border-[var(--fop-border)] text-[var(--fop-text-3)]">
                      Reject
                    </button>
                  </div>
                )}
                {correctingId === o.id && (
                  <div className="flex gap-2 mt-2" onClick={(e) => e.stopPropagation()} data-testid={`object-correct-form-${o.id}`}>
                    <input
                      value={correctionLabel}
                      onChange={(e) => setCorrectionLabel(e.target.value)}
                      className="flex-1 text-[11px] px-2 py-1 rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] text-[var(--fop-text)]"
                      data-testid={`object-correct-input-${o.id}`}
                    />
                    <button onClick={() => onSubmitCorrect(o.id)}
                            data-testid={`object-correct-submit-${o.id}`}
                            className="text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded bg-[var(--fop-cta-bg)] text-white">
                      Save
                    </button>
                    <button onClick={() => setCorrectingId(null)}
                            className="text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded border border-[var(--fop-border)] text-[var(--fop-text-3)]">
                      Cancel
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {showCreate && (
        <CreateObjectModal
          projectId={projectId} sheetId={sheetId}
          onClose={() => setShowCreate(false)}
          onCreated={(optimisticRow) => {
            setShowCreate(false);
            if (optimisticRow) {
              // Offline path — the server hasn't seen this yet; show it
              // locally as pending. reload() would just hit the network
              // and fail/return stale data while offline.
              setRows((prev) => [optimisticRow, ...(prev || [])]);
            } else {
              reload();
            }
            window.dispatchEvent(new CustomEvent("forgedops:objects-updated"));
          }}
        />
      )}
    </div>
  );
}

function CreateObjectModal({ projectId, sheetId, onClose, onCreated }) {
  const [type, setType] = useState("structure");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [meta, setMeta] = useState({});
  const [saving, setSaving] = useState(false);
  const setM = (k, v) => setMeta((prev) => ({ ...prev, [k]: v }));
  // FP-034 (2026-09-09 audit remediation) — stable id for this create
  // attempt, reused across manual retries (e.g. clicking "create"
  // again after a timeout of unknown outcome) so the server never
  // creates a duplicate object.
  const createOpIdRef = useRef(
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID() : `cop_${Date.now()}_${Math.floor(Math.random() * 1e9)}`,
  );

  const submit = async () => {
    if (!label.trim()) return;
    setSaving(true);
    const body = {
      type, label: label.trim(),
      description: description.trim() || null,
      sheet_id: sheetId || null,
      metadata: meta,
    };
    try {
      // FP-034 (2026-09-09 audit remediation) — object creation now
      // durably queues offline instead of only failing online, using
      // the SAME canonical v2 queue as markups/punch/pins (no new
      // queue). The entry is queued to disk (enqueue() is synchronous
      // localStorage write) BEFORE this function ever tells the user
      // it "saved" — the modal only closes after that write succeeds.
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        const localId = offlineEnqueue("object.create", {
          projectId, body: { ...body, client_op_id: createOpIdRef.current },
        });
        onCreated({
          id: localId, project_id: projectId, sheet_id: sheetId,
          type, label: label.trim(), description: description.trim() || null,
          metadata: meta, conflict_status: "none", status: "active",
          created_by_name: "You", created_at: new Date().toISOString(),
          _pending_sync: true,
        });
        return;
      }
      await api.post(`/projects/${projectId}/objects`,
        { ...body, client_op_id: createOpIdRef.current });
      onCreated(null);
    } catch (e) {
      // surface minimal error inline
      window.alert(e?.response?.data?.detail || "Failed to create");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="create-object-modal">
      <div className="w-full max-w-md bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--fop-border)]">
          <div className="font-display text-[var(--fop-text)] text-base">Add object</div>
          <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" data-testid="create-object-close"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <Field label="Type">
            <select value={type} onChange={(e) => setType(e.target.value)} data-testid="create-object-type"
              className="w-full bg-[var(--fop-surface-2)] border border-[var(--fop-border)] rounded px-2 py-2 text-sm">
              {OBJECT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Label">
            <input
              type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="DS-04 Curb Inlet"
              data-testid="create-object-label"
              className="w-full bg-[var(--fop-surface-2)] border border-[var(--fop-border)] rounded px-2 py-2 text-sm"
            />
          </Field>
          <Field label="Description (optional)">
            <textarea
              rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
              data-testid="create-object-description"
              className="w-full bg-[var(--fop-surface-2)] border border-[var(--fop-border)] rounded px-2 py-2 text-sm"
            />
          </Field>
          {type === "structure" && (
            <div className="grid grid-cols-2 gap-2">
              <Field label="RIM"><MetaInput v={meta.rim} k="rim" set={setM} placeholder="412.50" /></Field>
              <Field label="INV"><MetaInput v={meta.invert} k="invert" set={setM} placeholder="408.20" /></Field>
              <Field label="Pipe size"><MetaInput v={meta.pipe_size} k="pipe_size" set={setM} placeholder="18in" /></Field>
              <Field label="Material"><MetaInput v={meta.material} k="material" set={setM} placeholder="RCP" /></Field>
            </div>
          )}
          {type === "utility" && (
            <div className="grid grid-cols-2 gap-2">
              <Field label="Utility kind">
                <select value={meta.utility_kind || ""} onChange={(e) => setM("utility_kind", e.target.value)}
                  data-testid="create-object-utility-kind"
                  className="w-full bg-[var(--fop-surface-2)] border border-[var(--fop-border)] rounded px-2 py-2 text-sm">
                  <option value="">—</option>
                  <option value="water">Water</option>
                  <option value="sewer">Sewer</option>
                  <option value="storm">Storm</option>
                  <option value="gas">Gas</option>
                  <option value="electric">Electric</option>
                  <option value="communications">Communications</option>
                  <option value="fiber">Fiber</option>
                  <option value="telecom">Telecom</option>
                  <option value="unknown">Unknown</option>
                </select>
              </Field>
              <Field label="Owner"><MetaInput v={meta.owner} k="owner" set={setM} placeholder="City" /></Field>
              <Field label="Material"><MetaInput v={meta.material} k="material" set={setM} placeholder="DIP" /></Field>
              <Field label="Size"><MetaInput v={meta.size} k="size" set={setM} placeholder="12in" /></Field>
              <Field label="Depth (ft)"><MetaInput v={meta.depth} k="depth" set={setM} placeholder="4.5" /></Field>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-[var(--fop-border)]">
          <button onClick={onClose} className="text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)] px-3 py-2" data-testid="create-object-cancel">Cancel</button>
          <button
            onClick={submit} disabled={!label.trim() || saving}
            data-testid="create-object-submit"
            className="text-xs font-mono uppercase tracking-wider px-3 py-2 rounded bg-[var(--fop-primary)] text-black disabled:opacity-50"
          >{saving ? "Saving…" : "Create"}</button>
        </div>
      </div>
    </div>
  );
}

function MetaInput({ v, k, set, placeholder }) {
  return (
    <input
      type="text"
      value={v == null ? "" : v}
      onChange={(e) => set(k, e.target.value)}
      placeholder={placeholder}
      className="w-full bg-[var(--fop-surface-2)] border border-[var(--fop-border)] rounded px-2 py-1.5 text-sm"
    />
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">{label}</div>
      {children}
    </div>
  );
}

function FilterChip({ on, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded border transition-colors
        ${on ? "border-[var(--fop-primary)] text-[var(--fop-primary)] bg-[rgba(249,115,22,0.08)]"
            : "border-[var(--fop-border)] text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"}`}
      style={{ minHeight: 28 }}
    >
      {label}
    </button>
  );
}

/* -------------------- Activity -------------------- */
function ActivityTab({ projectId, sheetId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!sheetId) return;
    api.get(`/audit`, { params: { target_id: sheetId, limit: 50 } })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setRows([]));
  }, [sheetId]);
  if (rows === null) return <div className="p-4 text-xs text-[var(--fop-text-3)]">Loading…</div>;
  return (
    <div>
      <PanelHeader label={`Activity · ${rows.length}`} testid="activity-header" />
      {rows.length === 0 ? (
        <EmptyState
          title="No activity yet on this sheet"
          body="Every mutation — uploads, markups, measurements, pin drops, revisions, ack changes — writes an audit row. Activity will appear here as the sheet is used."
        />
      ) : (
        <ul data-testid="activity-list">
          {rows.slice(0, 100).map((a, i) => (
            <li key={a.id || i} className="px-3 py-2 border-b border-[var(--fop-border)]">
              <div className="text-xs text-[var(--fop-text)] truncate">{a.action || a.type}</div>
              <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5">
                {a.actor_name || a.actor_id || "—"}{a.created_at ? ` · ${new Date(a.created_at).toLocaleString()}` : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* -------------------- Shared bits -------------------- */
function PanelHeader({ label, right, testid }) {
  return (
    <div className="px-3 py-2 border-b border-[var(--fop-border)] flex items-baseline justify-between bg-[var(--fop-surface-2)]" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">{label}</div>
      {right}
    </div>
  );
}

function EmptyState({ title, body }) {
  return (
    <div className="p-5" data-testid="inspector-empty-state">
      <div className="text-sm text-[var(--fop-text)] font-medium mb-1.5">{title}</div>
      <div className="text-xs text-[var(--fop-text-3)] leading-relaxed">{body}</div>
    </div>
  );
}


/* -------------------- Iter 112 F-007 / Iter 116 P2 — Referenced by strip ---------- */
// Iter 116 P2 — every reference label is now a clickable destination so the
// bidirectional knowledge walk works without browser-back. Destinations
// match SpecSectionDetail.destinationFor (kept inline to avoid an extra import).
function _stripDest(r) {
  switch (r.target_type) {
    case "sheet": return `/sheet/${r.target_id}`;
    case "spec_section": return `/spec-section/${r.target_id}`;
    case "finding": return `/discoveries?focus=${r.target_id}`;
    case "intake_document": return `/intake?focus=${r.target_id}`;
    default: return null;
  }
}

function SheetReferencesStrip({ sheetId }) {
  const [refs, setRefs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!sheetId) return;
    let cancelled = false;
    setLoaded(false);
    api.get(`/references/sheet/${sheetId}`)
      .then((r) => { if (!cancelled) { setRefs(Array.isArray(r.data) ? r.data : []); setLoaded(true); } })
      .catch(() => { if (!cancelled) { setRefs([]); setLoaded(true); } });
    return () => { cancelled = true; };
  }, [sheetId]);
  if (!loaded || refs.length === 0) return null;
  return (
    <div className="px-3 py-2 border-b border-[var(--fop-border)] bg-[var(--fop-surface-2)]" data-testid="sheet-references-strip">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-1.5 inline-flex items-center gap-1.5">
        <Link2 size={11} /> Referenced by · {refs.length}
      </div>
      <ul className="space-y-1">
        {refs.slice(0, 6).map((r, i) => {
          const href = _stripDest(r);
          const inner = (
            <>
              <span className="font-mono uppercase tracking-wider text-[9px] text-[var(--fop-text-3)] mr-1.5">
                {(r.target_type || "ref").replace("_", " ")}
              </span>
              <span className="truncate">{r.label || r.target_id}</span>
            </>
          );
          return (
            <li key={i} className="text-[11px] text-[var(--fop-text)] truncate" data-testid={`sheet-ref-${i}`}>
              {href ? (
                <RouterLink to={href} className="hover:text-[var(--fop-primary)] hover:underline truncate inline-block max-w-full">
                  {inner}
                </RouterLink>
              ) : (
                <span className="truncate">{inner}</span>
              )}
            </li>
          );
        })}
        {refs.length > 6 && (
          <li className="text-[10px] font-mono text-[var(--fop-text-3)]">+ {refs.length - 6} more</li>
        )}
      </ul>
    </div>
  );
}
